export const EMPTY_PRICING = {
    configured: true,
    model: "fixed",
    currency: "IRR",
    minimumSeats: 1,
    maximumSeats: null,
    monthlyEnabled: true,
    monthlyAmount: null,
    annualEnabled: true,
    annualAmount: null,
};

const EMPTY_PRICE = {
    documentId: null,
    name: "",
    code: "",
    billingType: "contractual",
    billingInterval: "month",
    intervalCount: 1,
    currency: "IRR",
    amount: null,
    compareAtAmount: null,
    setupFee: 0,
    seatPricingMode: "fixed",
    includedSeats: 1,
    pricePerExtraSeat: 0,
    minimumSeats: 1,
    maximumSeats: null,
    effectiveFrom: "",
    effectiveUntil: "",
    isDefault: false,
    isActive: true,
    sortOrder: 0,
};

function numberOrNull(value) {
    return value === null || value === undefined || value === "" ? null : Number(value);
}

function rawPricePayload(price) {
    const dateTime = (value) => value ? new Date(value).toISOString() : null;
    return {
        documentId: price.documentId || undefined,
        name: price.name.trim(),
        code: price.code.trim(),
        billingType: price.billingType,
        billingInterval: price.billingInterval,
        intervalCount: Number(price.intervalCount || 1),
        currency: String(price.currency || "IRR").trim().toUpperCase(),
        amount: price.billingType === "contractual" ? null : String(price.amount ?? 0),
        compareAtAmount: price.compareAtAmount == null ? null : String(price.compareAtAmount),
        setupFee: String(price.setupFee || 0),
        seatPricingMode: price.seatPricingMode,
        includedSeats: Number(price.includedSeats || 0),
        pricePerExtraSeat: String(price.pricePerExtraSeat || 0),
        minimumSeats: Number(price.minimumSeats || 0),
        maximumSeats: price.maximumSeats == null ? null : Number(price.maximumSeats),
        effectiveFrom: dateTime(price.effectiveFrom),
        effectiveUntil: dateTime(price.effectiveUntil),
        isDefault: Boolean(price.isDefault),
        isActive: Boolean(price.isActive),
        sortOrder: Number(price.sortOrder || 0),
    };
}

export function pricingForForm(prices = []) {
    const active = prices.filter((price) => price.isActive !== false);
    if (!active.length) return {...EMPTY_PRICING, configured: false};
    const recurring = active.filter((price) => price.billingType === "recurring");
    const free = active.find((price) => price.billingType === "free");
    const contractual = active.find((price) => price.billingType === "contractual");
    const model = recurring.length
        ? (recurring.some((price) => price.seatPricingMode === "per_seat") ? "per_user" : "fixed")
        : free ? "free" : "custom";
    const preferred = recurring.find((price) => price.isDefault)
        || recurring[0]
        || free
        || contractual
        || active[0];
    const monthly = recurring.find((price) => price.billingInterval === "month");
    const annual = recurring.find((price) => price.billingInterval === "year");
    const amount = (price) => numberOrNull(
        model === "per_user" ? price?.pricePerExtraSeat : price?.amount
    );
    return {
        ...EMPTY_PRICING,
        configured: true,
        model,
        currency: String(preferred?.currency || "IRR").toUpperCase(),
        minimumSeats: Number(preferred?.minimumSeats || 1),
        maximumSeats: numberOrNull(preferred?.maximumSeats),
        monthlyEnabled: Boolean(monthly),
        monthlyAmount: amount(monthly),
        annualEnabled: Boolean(annual),
        annualAmount: amount(annual),
    };
}

function canonicalPrice(plan, existing, suffix, data) {
    return {
        ...EMPTY_PRICE,
        ...existing,
        documentId: existing?.documentId || null,
        name: existing?.name || `${plan.name} ${suffix}`,
        code: existing?.code || `${plan.code}-${data.codeSuffix}`,
        compareAtAmount: null,
        setupFee: 0,
        includedSeats: data.seatPricingMode === "per_seat" ? 0 : 1,
        pricePerExtraSeat: 0,
        effectiveFrom: null,
        effectiveUntil: null,
        maximumSeats: null,
        isActive: true,
        ...data,
    };
}

export function pricingPayload(pricing, existingPrices, plan) {
    const unused = [...existingPrices];
    const take = (predicate) => {
        const index = unused.findIndex(predicate);
        return index < 0 ? null : unused.splice(index, 1)[0];
    };
    const common = {
        currency: String(pricing.currency || "IRR").trim().toUpperCase(),
        intervalCount: 1,
        minimumSeats: Number(pricing.minimumSeats || 1),
        maximumSeats: pricing.maximumSeats == null ? null : Number(pricing.maximumSeats),
    };
    const selected = [];

    if (pricing.model === "free") {
        const existing = take((price) => price.billingType === "free");
        selected.push(canonicalPrice(plan, existing, "Free", {
            ...common,
            codeSuffix: "free",
            billingType: "free",
            billingInterval: "none",
            amount: 0,
            seatPricingMode: "fixed",
            minimumSeats: 1,
            maximumSeats: 1,
            isDefault: true,
            sortOrder: 0,
        }));
    } else if (pricing.model === "custom") {
        const existing = take((price) => price.billingType === "contractual");
        selected.push(canonicalPrice(plan, existing, "Custom", {
            ...common,
            codeSuffix: "custom",
            billingType: "contractual",
            billingInterval: "none",
            amount: null,
            seatPricingMode: "contractual",
            minimumSeats: 1,
            maximumSeats: null,
            isDefault: true,
            sortOrder: 0,
        }));
    } else {
        const seatPricingMode = pricing.model === "per_user" ? "per_seat" : "fixed";
        const addRecurring = (billingInterval, amount, sortOrder, isDefault) => {
            const existing = take((price) => (
                price.billingType === "recurring"
                && price.billingInterval === billingInterval
                && price.seatPricingMode === seatPricingMode
            ));
            selected.push(canonicalPrice(plan, existing, billingInterval === "month" ? "Monthly" : "Annual", {
                ...common,
                codeSuffix: `${pricing.model}-${billingInterval}`,
                billingType: "recurring",
                billingInterval,
                amount: seatPricingMode === "per_seat" ? 0 : amount,
                seatPricingMode,
                pricePerExtraSeat: seatPricingMode === "per_seat" ? amount : 0,
                minimumSeats: seatPricingMode === "per_seat" ? common.minimumSeats : 1,
                maximumSeats: seatPricingMode === "per_seat" ? common.maximumSeats : null,
                isDefault,
                sortOrder,
            }));
        };
        if (pricing.monthlyEnabled) {
            addRecurring("month", pricing.monthlyAmount, 0, true);
        }
        if (pricing.annualEnabled) {
            addRecurring("year", pricing.annualAmount, 1, !pricing.monthlyEnabled);
        }
    }

    const inactive = unused.map((price, index) => ({
        ...price,
        isActive: false,
        isDefault: false,
        sortOrder: selected.length + index,
    }));
    return [...selected, ...inactive].map(rawPricePayload);
}
